/**
 * FilterRulesManager - Dialog for managing automatic filter rules
 *
 * Features:
 * - List all filter rules with enable/disable toggles
 * - Drag to reorder priority
 * - Edit, duplicate, delete, and run actions
 * - Create new rules
 */

import {
	Copy,
	GripVertical,
	MoreHorizontal,
	Pencil,
	Play,
	Plus,
	Trash2,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import {
	DragDropContext,
	Droppable,
	Draggable,
	type DropResult,
} from '@hello-pangea/dnd';
import type { FilterRule } from '@/api/filters';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import {
	useDeleteFilterRule,
	useDuplicateFilterRule,
	useFilterRules,
	useReorderFilterRules,
	useRunFilterRule,
	useToggleFilterRule,
} from '@/hooks/useFilterRules';
import { FilterRuleDialog } from './FilterRuleDialog';

interface FilterRulesManagerProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

type ViewMode = 'list' | 'create' | 'edit';

interface RunResultState {
	show: boolean;
	ruleId: number;
	ruleName: string;
	dryRun: boolean;
	matched: number;
	total: number;
	error?: string;
}

export function FilterRulesManager({ open, onOpenChange }: FilterRulesManagerProps) {
	const [mode, setMode] = useState<ViewMode>('list');
	const [editingRule, setEditingRule] = useState<FilterRule | null>(null);
	const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
	const [runResult, setRunResult] = useState<RunResultState | null>(null);

	const { data: rules = [], isLoading } = useFilterRules();
	const toggleMutation = useToggleFilterRule();
	const deleteMutation = useDeleteFilterRule();
	const duplicateMutation = useDuplicateFilterRule();
	const reorderMutation = useReorderFilterRules();
	const runMutation = useRunFilterRule();

	// Sort rules by priority
	const sortedRules = [...rules].sort((a, b) => a.priority - b.priority);

	// Reset state when dialog closes
	useEffect(() => {
		if (!open) {
			setMode('list');
			setEditingRule(null);
			setDeleteConfirmId(null);
			setRunResult(null);
		}
	}, [open]);

	// Clear delete confirm after timeout
	useEffect(() => {
		if (deleteConfirmId !== null) {
			const timer = setTimeout(() => setDeleteConfirmId(null), 3000);
			return () => clearTimeout(timer);
		}
	}, [deleteConfirmId]);

	function handleStartCreate() {
		setEditingRule(null);
		setMode('create');
	}

	function handleStartEdit(rule: FilterRule) {
		setEditingRule(rule);
		setMode('edit');
	}

	function handleDialogClose() {
		setMode('list');
		setEditingRule(null);
	}

	function handleToggle(rule: FilterRule) {
		toggleMutation.mutate(rule.id);
	}

	const handleDelete = useCallback(
		(ruleId: number) => {
			if (deleteConfirmId !== ruleId) {
				setDeleteConfirmId(ruleId);
				return;
			}
			deleteMutation.mutate(ruleId);
			setDeleteConfirmId(null);
		},
		[deleteConfirmId, deleteMutation]
	);

	function handleDuplicate(rule: FilterRule) {
		duplicateMutation.mutate({ id: rule.id });
	}

	function handleRun(rule: FilterRule, dryRun: boolean) {
		runMutation.mutate(
			{ id: rule.id, options: { dryRun } },
			{
				onSuccess: (result) => {
					setRunResult({
						show: true,
						ruleId: rule.id,
						ruleName: rule.name,
						dryRun: result.dryRun,
						matched: result.matchedLinks,
						total: result.totalLinks,
					});
				},
				onError: (error) => {
					setRunResult({
						show: true,
						ruleId: rule.id,
						ruleName: rule.name,
						dryRun,
						matched: 0,
						total: 0,
						error: error instanceof Error ? error.message : 'Unknown error',
					});
				},
			}
		);
	}

	function handleDragEnd(result: DropResult) {
		if (!result.destination) return;
		if (result.destination.index === result.source.index) return;

		const reordered = Array.from(sortedRules);
		const [removed] = reordered.splice(result.source.index, 1);
		reordered.splice(result.destination.index, 0, removed);

		const orderedIds = reordered.map((r) => r.id);
		reorderMutation.mutate(orderedIds);
	}

	// Render the run result dialog
	function renderRunResult() {
		if (!runResult?.show) return null;

		return (
			<Dialog open={runResult.show} onOpenChange={() => setRunResult(null)}>
				<DialogContent className="max-w-md">
					<DialogHeader>
						<DialogTitle>
							{runResult.dryRun ? 'Dry Run' : 'Run'} Results
						</DialogTitle>
						<DialogDescription>
							Rule: {runResult.ruleName}
						</DialogDescription>
					</DialogHeader>

					<div className="py-4">
						{runResult.error ? (
							<div className="text-destructive">
								<p className="font-medium">Error</p>
								<p className="text-sm">{runResult.error}</p>
							</div>
						) : (
							<div className="space-y-2">
								<p>
									<span className="font-medium">{runResult.matched}</span> of{' '}
									<span className="font-medium">{runResult.total}</span> links matched
								</p>
								{runResult.dryRun ? (
									<p className="text-sm text-muted-foreground">
										This was a dry run - no actions were executed.
									</p>
								) : (
									<p className="text-sm text-muted-foreground">
										Actions have been executed on matching links.
									</p>
								)}
							</div>
						)}
					</div>

					<DialogFooter>
						<Button onClick={() => setRunResult(null)}>Close</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		);
	}

	// Render a single rule item
	function renderRuleItem(rule: FilterRule, index: number) {
		const isDeleting = deleteMutation.isPending && deleteConfirmId === rule.id;
		const isRunning = runMutation.isPending;

		return (
			<Draggable key={rule.id} draggableId={String(rule.id)} index={index}>
				{(provided, snapshot) => (
					<div
						ref={provided.innerRef}
						{...provided.draggableProps}
						className={cn(
							'flex items-center gap-3 p-3 rounded-lg border bg-card transition-colors',
							snapshot.isDragging && 'shadow-lg ring-2 ring-primary',
							!rule.enabled && 'opacity-60'
						)}
					>
						{/* Drag handle */}
						<div
							{...provided.dragHandleProps}
							className="cursor-grab active:cursor-grabbing"
						>
							<GripVertical className="size-4 text-muted-foreground" />
						</div>

						{/* Enable/disable toggle */}
						<Switch
							checked={rule.enabled}
							onCheckedChange={() => handleToggle(rule)}
							disabled={toggleMutation.isPending}
						/>

						{/* Rule info */}
						<div className="flex-1 min-w-0">
							<p className="font-medium truncate">{rule.name}</p>
							{rule.description && (
								<p className="text-xs text-muted-foreground truncate">
									{rule.description}
								</p>
							)}
							<div className="flex items-center gap-2 mt-1">
								<Badge variant="outline" className="text-xs">
									{rule.conditions.length} condition{rule.conditions.length !== 1 ? 's' : ''}
								</Badge>
								<Badge variant="outline" className="text-xs">
									{rule.actions.length} action{rule.actions.length !== 1 ? 's' : ''}
								</Badge>
								{rule.matchCount > 0 && (
									<span className="text-xs text-muted-foreground">
										{rule.matchCount} matches
									</span>
								)}
							</div>
						</div>

						{/* Actions dropdown */}
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button variant="ghost" size="icon" className="size-8">
									<MoreHorizontal className="size-4" />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end">
								<DropdownMenuItem onClick={() => handleStartEdit(rule)}>
									<Pencil className="size-4 mr-2" />
									Edit
								</DropdownMenuItem>
								<DropdownMenuItem onClick={() => handleDuplicate(rule)}>
									<Copy className="size-4 mr-2" />
									Duplicate
								</DropdownMenuItem>
								<DropdownMenuSeparator />
								<DropdownMenuItem
									onClick={() => handleRun(rule, true)}
									disabled={isRunning}
								>
									<Play className="size-4 mr-2" />
									Dry Run (Preview)
								</DropdownMenuItem>
								<DropdownMenuItem
									onClick={() => handleRun(rule, false)}
									disabled={isRunning}
								>
									<Play className="size-4 mr-2" />
									Run Now
								</DropdownMenuItem>
								<DropdownMenuSeparator />
								<DropdownMenuItem
									onClick={() => handleDelete(rule.id)}
									disabled={isDeleting}
									className={cn(
										'text-destructive focus:text-destructive',
										deleteConfirmId === rule.id && 'bg-destructive text-destructive-foreground'
									)}
								>
									<Trash2 className="size-4 mr-2" />
									{deleteConfirmId === rule.id ? 'Confirm Delete?' : 'Delete'}
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
					</div>
				)}
			</Draggable>
		);
	}

	// Main content
	if (mode !== 'list') {
		return (
			<FilterRuleDialog
				open={open}
				onOpenChange={onOpenChange}
				rule={editingRule}
				onClose={handleDialogClose}
			/>
		);
	}

	return (
		<>
			<Dialog open={open} onOpenChange={onOpenChange}>
				<DialogContent className="max-w-2xl max-h-[90vh] p-0 gap-0 overflow-hidden">
					<DialogHeader className="px-6 pt-6 pb-4 border-b">
						<DialogTitle className="text-xl">Filter Rules</DialogTitle>
						<DialogDescription>
							Automatic rules that match and process your links. Rules run in priority order
							(drag to reorder).
						</DialogDescription>
					</DialogHeader>

					<ScrollArea className="flex-1 max-h-[calc(90vh-12rem)]">
						<div className="px-6 py-4 space-y-2">
							{isLoading ? (
								<div className="text-center py-8 text-muted-foreground">
									Loading rules...
								</div>
							) : sortedRules.length === 0 ? (
								<div className="text-center py-8 text-muted-foreground">
									No filter rules yet. Create one to automatically process incoming links!
								</div>
							) : (
								<DragDropContext onDragEnd={handleDragEnd}>
									<Droppable droppableId="rules">
										{(provided) => (
											<div
												ref={provided.innerRef}
												{...provided.droppableProps}
												className="space-y-2"
											>
												{sortedRules.map((rule, index) => renderRuleItem(rule, index))}
												{provided.placeholder}
											</div>
										)}
									</Droppable>
								</DragDropContext>
							)}
						</div>
					</ScrollArea>

					<DialogFooter className="px-6 py-4 border-t">
						<Button variant="outline" onClick={() => onOpenChange(false)}>
							Close
						</Button>
						<Button onClick={handleStartCreate}>
							<Plus className="size-4 mr-2" />
							New Rule
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{renderRunResult()}
		</>
	);
}
